'use client';
import React, { useEffect, useRef } from 'react';

const LEARNINGS = [
  {
    num: '01',
    title: 'What SWOT is & why it endures',
    desc: 'Understand why SWOT remains relevant in modern decision-making and how to leverage its full power.',
    span: 2,
    accent: true,
  },
  {
    num: '02',
    title: 'Internal vs. External factors',
    desc: 'Distinguish strengths and weaknesses from opportunities and threats with precision.',
    span: 1,
    accent: false,
  },
  {
    num: '03',
    title: 'Step-by-step SWOT process',
    desc: 'A proven, collaborative process to conduct an effective SWOT analysis from start to finish.',
    span: 1,
    accent: false,
  },
  {
    num: '04',
    title: 'Data gathering best practices',
    desc: 'Techniques for collecting and validating both internal and external data sources.',
    span: 1,
    accent: false,
  },
  {
    num: '05',
    title: 'Insights → Strategic actions',
    desc: 'Convert SWOT findings into concrete, prioritized strategic actions your team can execute.',
    span: 1,
    accent: false,
  },
  {
    num: '06',
    title: 'Common mistakes to avoid',
    desc: 'The pitfalls most teams fall into and how to sidestep them for more accurate analysis.',
    span: 1,
    accent: false,
  },
  {
    num: '07',
    title: 'Advanced SWOT techniques',
    desc: 'Elevate your analysis using complementary frameworks like PESTLE, Porter\'s Five Forces, and more.',
    span: 1,
    accent: false,
  },
  {
    num: '08',
    title: 'Embed SWOT into daily planning',
    desc: 'Make SWOT a living part of your strategic planning and execution cycles, not a one-time exercise.',
    span: 2,
    accent: true,
  },
];

export default function LearnSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    const el = sectionRef?.current;
    if (el) {
      el?.querySelectorAll('.reveal-up, .reveal-blur, .stagger-grid')?.forEach((child) => observer?.observe(child));
      observer?.observe(el);
    }
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="learn"
      className="relative w-full py-28 px-6 lg:px-12 border-t border-white/5 overflow-hidden"
      style={{ background: 'var(--surface)' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div>
            <span className="font-mono text-xs text-accent uppercase tracking-widest reveal-up block mb-4">What You Will Learn</span>
            <h2 className="font-manrope text-5xl lg:text-7xl font-semibold tracking-tight leading-[0.9]">
              <span className="block reveal-text-line">8 Core</span>
              <span className="block text-foreground/30 reveal-text-line" style={{ transitionDelay: '0.1s' }}>Capabilities</span>
            </h2>
          </div>
          <p className="reveal-blur font-mono text-foreground/35 text-sm max-w-xs text-right">
            // STRATEGY // ANALYSIS // ACTION
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden stagger-grid">
          {LEARNINGS?.map((item) => (
            <div
              key={item?.num}
              className={`
                group relative p-7 flex flex-col justify-between cursor-default overflow-hidden
                hover:bg-accent/[0.04] transition-colors duration-500
                ${item?.span === 2 ? 'lg:col-span-2' : 'lg:col-span-1'}
                ${item?.accent ? 'border-accent/10' : ''}
                hover-card-shine
              `}
              style={{ background: 'var(--background)', minHeight: '180px' }}
            >
              {/* Accent glow for featured cards */}
              {item?.accent && (
                <div
                  className="absolute top-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.5), transparent)' }}
                />
              )}

              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs text-foreground/25 uppercase tracking-widest">{item?.num}</span>
                  {item?.accent && (
                    <span className="text-[10px] font-mono text-accent bg-accent/10 border border-accent/20 px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Key
                    </span>
                  )}
                </div>
                <h3 className="font-manrope text-base font-semibold text-foreground tracking-tight group-hover:text-accent transition-colors duration-300">
                  {item?.title}
                </h3>
                <p className="text-foreground/45 text-sm leading-relaxed font-light">
                  {item?.desc}
                </p>
              </div>

              {/* Bottom accent line */}
              <div
                className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500"
                style={{ background: 'linear-gradient(to right, rgba(201,168,76,0.6), transparent)' }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}